import { Category } from './category.types';
import { ColorType } from './color.types';
import { DeliveryOptionsWithTotals, DeliveryServiceType } from './delivery.types';
import { IAppliedDiscountCode } from './discount-code.types';
import { MaterialType } from './material.types';
import { OfferType } from './offer.types';
import { PricePerShippingMethods } from './order.types';
import { ShopType } from './shop.types';
import { UserType } from './user.types';
import { IUserPromoCode } from './user-promo-code.types';

export type ProductUploadUrlType = {
  url: string;
  fileName: string;
};

export type ProductAttributesType = {
  size?: string;
};

export type ProductImageType = {
  id: number;
  isLoading?: boolean;
  fileName: string;
  name?: string;
  type?: string;
  thumbnail: string;
  gallery: string;
  original: string;
  title: string;
  blurHash?: string;
};

export enum ProductStatus {
  PUBLISHED = 'published',
  RESERVED = 'reserved',
  SOLD = 'sold',
  DELETED = 'deleted',
  HIDDEN = 'hidden',
  NOT_VISIBLE = 'not-visible',
}

export type ProductStatusType = 'published' | 'reserved' | 'sold' | 'deleted' | 'hidden' | 'not-visible';

export type ProductPriceDropType = {
  referencePrice: number;
  percentage: number;
  expiresAt: string;
};

export type ProductType = {
  _id: number;
  deletedBy: string | number;
  pinnedAt: boolean;
  shopId: number;
  attributes: ProductAttributesType;
  createdAt: Date;
  updatedAt: Date;
  id: number;
  mongo_id: number;
  shop_id: number;
  brand_id: number;
  brand: string;
  name: string;
  slug: string;
  description: string;
  translatedDescription?: string;
  price: string;
  priceDrop?: ProductPriceDropType | null;
  /** Present only for buyers in the bumped-items test (see bumpedItems on list responses). */
  bumped?: boolean;
  currency: string;
  status: ProductStatusType;
  quantity: number;
  images: ProductImageType[];
  shipping: DeliveryServiceType;
  size: string | number;
  tags: string[];
  location: string;
  address: string;
  deleted_by: string | number;
  pinned_at: boolean;
  created_at: Date;
  updated_at: Date;
  views: number;
  isCharityProduct: boolean;
  uploadUrl: ProductUploadUrlType[];
  shop?: ShopType;
  likeCount: number;
  isOwner: boolean;
  /**
   * Attribution, not a product attribute: set on cards served by the Advec home feed so a click can
   * be traced to the /recommend page that produced it. Rides on the card so it survives the products
   * cache. Absent everywhere else.
   */
  recommendationId?: string;
  /**
   * Attribution: the feed arm that served this card. Set on every home-feed card regardless of arm,
   * per page — a later page can be served by a different engine when a provider falls back.
   */
  recommendationEngine?: HomeFeedEngine;
  user?: UserType;
  hasLiked: boolean;
  shippingOptionsWithTotals: DeliveryOptionsWithTotals;
  included_shipping_price?: string;
  flags?: any;
  colors_id_map?: number[];
  materials_id_map?: number[];
  condition_id?: number;
  categories_map?: number[];
  category_id?: number;
  categories?: Category[];
  condition?: ProductCondition;
  colors?: ColorType[];
  materials?: MaterialType[];
  liked?: boolean;
  buyerProtectionFeeEnabled?: boolean;
  buyerSideFeesPercentage?: number;
  buyerSideFeesFixed?: number;
  firstPublishedAt?: string;
  aiTags?: string;
  tagged?: boolean;
  productTags?: string[] | null;
  offer?: OfferType;
  priceWithoutOffer?: string;
  isFirstListing?: boolean;
  isSecondListing?: boolean;
  acceptedOffersCount?: number;
  isNewLister?: boolean;
  orderInProgress?: boolean;
};

export type ProductCondition = {
  id: number;
  name: string;
  description: string;
};

export type ProductListFilter = {
  id: number;
  label: string;
  isBrand?: boolean;
};

export type CampaignProductsType = {
  title: string;
  active?: boolean;
  id?: number;
  products: ProductType[];
};

export type HomeFeedEngine = 'legacy-postgres' | 'advec' | 'yaga-search';

export const HOME_FEED_ENGINES: readonly HomeFeedEngine[] = ['legacy-postgres', 'advec', 'yaga-search'];

export type HomeFeedResponse = {
  offset: number;
  total: number;
  list: ProductType[];
  shops: unknown[];
  recommendationEngine: HomeFeedEngine;
  /** Advec's attribution id for this page of the feed. Present on the advec arm only. */
  recommendationId?: string;
  experiment?: {
    inExperiment: boolean;
    experimentId?: string;
    variationId?: string;
  };
  /** bumped_items assignment, resolved server-side; used to fire experiment_viewed client-side. */
  bumpedItems?: BumpedItemsExposure;
};

export type BumpedItemsExposure = {
  enabled: boolean;
  inExperiment: boolean;
  experimentId?: string;
  variationId?: string;
};

export interface ISubTotals {
  appliedPromoCodes: IUserPromoCode[];
  appliedDiscountCodes: IAppliedDiscountCode[];
  productPrice: string;
  productDiscountAmount: string;
  shippingCost: PricePerShippingMethods;
  shippingDiscountAmount: PricePerShippingMethods;
  feeCost: PricePerShippingMethods;
  feeDiscountAmount?: PricePerShippingMethods;
  totalCost: PricePerShippingMethods;
  totalDiscount: PricePerShippingMethods;
  buyerSideFeesFixed: number;
  buyerSideFeesPercentage: number;
}

export interface ISubTotalsParams {
  productId: string;
  discountCode?: string;
}

export interface SubTotalData {
  productPrice: string;
  productDiscountAmount: string;
  shippingCost: PricePerShippingMethods;
  shippingDiscountAmount: PricePerShippingMethods;
}

export type ProductActionReasonType = {
  name: string;
  type: string;
};

export type DeleteReasonsType = ProductActionReasonType;

export type ProductAiPrefillData = {
  description?: string;
  category?: {
    id: number;
    title: string;
    parent?: number;
    categoryMap: number[];
  };
  condition?: {
    id: number;
    name: string;
    description?: string;
  };
  brand?: {
    id: number;
    name: string;
  };
  size?: {
    id: number;
    size: string;
  };
  colors: {
    id: number;
    name: string;
  }[];
  materials: {
    id: number;
    name: string;
  }[];
};
